import { FormEvent, useContext, useReducer } from 'react';
import { useNavigate } from 'react-router';

import { Form, FormInputWrapper } from '@/components/old';
import Button from '@/components/old/Button';

import AuthContext from '../../contexts/Auth/AuthContext';
import ModalContext from '../../contexts/Modal/ModalContext';
import { ModalName } from '../../contexts/Modal/modalTypes';

import { useTipUserList } from './useTipUserList';

enum LOGIN {
  USER_INPUT = 'USER_INPUT',
  INPUT_BLUR = 'INPUT_BLUR',
}

interface InputState {
  value: string;
  isValid: boolean;
}

interface Action {
  type: LOGIN;
  payload?: string;
}

const emailReducer = (state: InputState, action: Action): InputState => {
  if (action.type === LOGIN.USER_INPUT) {
    return {
      value: action.payload as string,
      isValid: action.payload?.includes('@') ?? false,
    };
  }
  if (action.type === LOGIN.INPUT_BLUR) {
    return {
      value: state.value,
      isValid: state.value.includes('@'),
    };
  }
  return { value: '', isValid: false };
};

const passwordReducer = (state: InputState, action: Action): InputState => {
  if (action.type === LOGIN.USER_INPUT) {
    return {
      value: action.payload as string,
      isValid: action.payload ? action.payload?.trim().length > 6 : false,
    };
  }
  if (action.type === LOGIN.INPUT_BLUR) {
    return {
      value: state.value,
      isValid: state.value.trim().length > 6,
    };
  }
  return { value: '', isValid: false };
};

export const BasicProvider: React.FC = () => {
  const authContext = useContext(AuthContext);
  const modalContext = useContext(ModalContext);
  const users = useTipUserList();
  const navigate = useNavigate();
  const [emailState, dispatchEmail] = useReducer(emailReducer, {
    value: '',
    isValid: false,
  });
  const [passwordState, dispatchPassword] = useReducer(passwordReducer, {
    value: import.meta.env.VITE_DEFAULT_PASSWORD || '',
    isValid: false,
  });

  const emailChangeHandler = (event: FormEvent<HTMLInputElement>): void => {
    dispatchEmail({
      type: LOGIN.USER_INPUT,
      payload: event.currentTarget.value,
    });
  };

  const passwordChangeHandler = (event: FormEvent<HTMLInputElement>): void => {
    dispatchPassword({
      type: LOGIN.USER_INPUT,
      payload: event.currentTarget.value,
    });
  };

  const validateEmailHandler = (): void => {
    dispatchEmail({ type: LOGIN.INPUT_BLUR });
  };

  const validatePasswordHandler = (): void => {
    dispatchPassword({ type: LOGIN.INPUT_BLUR });
  };

  const submitHandler = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    await authContext.loginWithPassword(passwordState.value, emailState.value);

    if (authContext.userData !== null && !authContext.userData?.date_of_birth) {
      modalContext.renderSpecificModal({ modalName: ModalName.BirthdayAlert, modalProps: { navigate } });
    }
  };

  return (
    <Form onSubmit={submitHandler} id="login-form">
      <input type="hidden" name="_token" value="" />
      <FormInputWrapper label="E-Mail або ІНН" className={emailState.isValid === false ? 'error' : ''}>
        <input
          id="login"
          className="form-input__input"
          name="login"
          required
          type="email"
          value={emailState.value}
          onChange={emailChangeHandler}
          onBlur={validateEmailHandler}
          list="users"
        />
        <datalist id="users">
          {users?.map((email) => (
            <option value={email} key={email} />
          ))}
        </datalist>
      </FormInputWrapper>
      <FormInputWrapper label="Пароль" className={passwordState.isValid === false ? 'error' : ''}>
        <input
          id="password"
          type="password"
          className="form-input__input"
          name="password"
          required
          value={passwordState.value}
          onChange={passwordChangeHandler}
          onBlur={validatePasswordHandler}
        />
      </FormInputWrapper>
      <Button type="submit" isMarginAuto={true}>
        Вхід
      </Button>
    </Form>
  );
};
